import { PlusIcon } from '@phosphor-icons/react';
import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';

import { PageHeader } from '../../../components/PageHeader';
import { Pagination } from '../../../components/pagination/Pagination';
import { ITableHeader, Table, TableSortOrder } from '../../../components/table/Table';
import { IPaginationVariables, usePagination } from '../../../hooks/usePagination';
import { formatDate } from '../../../utils/date';
import { Breadcrumb } from '../../../components/Breadcrumb';
import { LinkButton } from '../../../components/button/ButtonLink';
import { PageHeading } from 'components/PageHeading';
import { ListPage } from 'components/page-templates/ListPage';
import { getIncassos } from '../models';

type IncassoSortBy = 'createdAt' | 'status' | 'trackingId';

const INCASSO_HEADERS: ITableHeader[] = [
  {
    id: 'id',
    name: 'ID',
  },
  {
    id: 'trackingId',
    name: 'Tracking ID',
    sortable: true,
  },
  {
    id: 'status',
    name: 'Status',
    sortable: true,
  },
  {
    id: 'customer',
    name: 'Klant',
  },
  {
    id: 'partner',
    name: 'Partner',
  },
  {
    id: 'createdAt',
    name: 'Aangemaakt op',
    sortable: true,
  },
];

const IncassosPage = () => {
  const [sortBy, setSortBy] = useState<IncassoSortBy | undefined>(undefined);
  const [sortOrder, setSortOrder] = useState<TableSortOrder | undefined>(undefined);
  const pageFetcher = useCallback(
    async (variables: IPaginationVariables) => {
      return getIncassos(variables.cursor, variables.take, sortBy, sortOrder);
    },
    [sortBy, sortOrder],
  );
  const page = usePagination({
    key: 'id',
    pageSize: 50,
    initialCursor: undefined,
    fetcher: pageFetcher,
  });

  const title = 'Incassos';
  return (
    <>
      <PageHeader title={title} />

      <ListPage
        heading={
          <PageHeading
            noMarginBottom
            leftSide={
              <Breadcrumb
                parentItem={{
                  name: 'Financieel',
                  to: '/internal/finance',
                }}
                currentItem={title}
              />
            }
            rightSide={
              <LinkButton to="new" color="primary" iconLeft={<PlusIcon className="button-icon" />}>
                Nieuwe incasso
              </LinkButton>
            }
          />
        }
        panel
        pagination={
          <Pagination
            hasPrevious={page.hasPrevious}
            previous={page.previous}
            hasNext={page.hasNext}
            next={page.next}
            isFetching={page.isFetching}
          />
        }
      >
        <Table
          className="h-full"
          idKey="id"
          headers={INCASSO_HEADERS}
          data={page.data}
          error={page.error}
          onRetry={page.retry}
          isLoading={page.isFetching}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortChange={(id, order) => {
            setSortBy(id as IncassoSortBy | undefined);
            setSortOrder(order);
            page.reset();
          }}
          mapData={(incasso) => {
            return [
              <Link to={`${incasso.id}`} className="link-text">
                {incasso.internalNumber}
              </Link>,
              <Link to={`${incasso.id}`} className="link-text">
                {incasso.trackingId}
              </Link>,
              incasso.status,
              <Link to={`/internal/customers/${incasso.customer.id}/general`} className="link-text">
                {incasso.customer.name}
              </Link>,
              incasso.partner,
              formatDate(incasso.createdAt),
            ];
          }}
        />
      </ListPage>
    </>
  );
};

export default IncassosPage;
